import { useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { RemoveModuleFromProgramme } from "../../../../../services/ProgramService";

/**
 * Drives the Version Modules table's row selection and bulk "Unlink Modules"
 * confirm -> progress -> cancel-remaining flow, split out of `useVersionModules`
 * so that hook stays focused on module data/filters/sorting/pagination.
 * @param {string | number} programmeId PMC programme id.
 * @param {string | number} versionId Version id to scope unlinks to.
 * @param {boolean} isLocked Whether the version's settings are locked - blocks unlinking.
 * @param {Array<string|number>} allMatchingIds Ids of every row matching the current filters/sort (for select-all).
 * @param {string} filterSignature Changes whenever search/filters change, to clear stale selection.
 * @param {boolean} isRefreshing Whether module data is (re)loading - selection isn't pruned against it meanwhile.
 * @param {() => Promise<void>} onUnlinked Called once a bulk unlink attempt finishes, to refresh module data.
 */
function useVersionModuleBulkActions({ programmeId, versionId, isLocked, allMatchingIds, filterSignature, isRefreshing, onUnlinked }) {
    const [selectedIds, setSelectedIds] = useState([]);
    const [confirmation, setConfirmation] = useState(null);
    const [progress, setProgress] = useState(null);
    const cancelRemainingRef = useRef(false);
    const progressTimeoutRef = useRef(null);

    const isProcessing = Boolean(progress);

    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const allMatchingSelected = allMatchingIds.length > 0 && allMatchingIds.every((id) => selectedIdSet.has(id));
    const someMatchingSelected = selectedIds.length > 0 && !allMatchingSelected;

    useEffect(() => {
        setSelectedIds([]);
    }, [filterSignature]);

    /* Prunes any selected id that's dropped out of the current matching set for a reason other than a filter change (e.g. a background refresh), so a stale id never reaches the unlink request. */
    useEffect(() => {
        if (isRefreshing) {
            return;
        }

        setSelectedIds((current) => {
            const matchingIdSet = new Set(allMatchingIds);
            const pruned = current.filter((id) => matchingIdSet.has(id));
            return pruned.length === current.length ? current : pruned;
        });
    }, [allMatchingIds, isRefreshing]);

    useEffect(() => {
        return () => {
            if (progressTimeoutRef.current) {
                clearTimeout(progressTimeoutRef.current);
            }
        };
    }, []);

    const toggleRow = (rowId) => {
        setSelectedIds((current) =>
            current.includes(rowId) ? current.filter((value) => value !== rowId) : [...current, rowId]
        );
    };

    const toggleAllMatching = () => {
        setSelectedIds(allMatchingSelected ? [] : allMatchingIds);
    };

    const startConfirmation = (ids) => {
        if (isLocked) {
            return;
        }
        setConfirmation({ ids });
    };

    const cancelRemaining = () => {
        cancelRemainingRef.current = true;
    };

    const unlinkOne = async (moduleId) => {
        await RemoveModuleFromProgramme({ PId: programmeId, MId: moduleId, version_id: versionId });
    };

    const runConfirmedUnlink = async () => {
        if (isLocked || isProcessing) {
            setConfirmation(null);
            return;
        }

        const ids = confirmation?.ids;

        if (!ids?.length) {
            setConfirmation(null);
            return;
        }

        cancelRemainingRef.current = false;
        setConfirmation(null);

        const failedIds = [];
        let attemptedCount = 0;

        try {
            for (let index = 0; index < ids.length; index += 1) {
                if (cancelRemainingRef.current) {
                    break;
                }

                attemptedCount += 1;
                setProgress({ label: "Unlinking", current: index + 1, total: ids.length });

                try {
                    // eslint-disable-next-line no-await-in-loop
                    await unlinkOne(ids[index]);
                } catch (error) {
                    console.error("useVersionModuleBulkActions.js->runConfirmedUnlink->Error", error);
                    failedIds.push(ids[index]);
                }
            }

            setSelectedIds(failedIds);
            await onUnlinked();

            const unlinkedCount = attemptedCount - failedIds.length;

            if (failedIds.length > 0) {
                Swal.fire({
                    icon: "warning",
                    title: "Some modules couldn't be unlinked",
                    text: `${failedIds.length} module${failedIds.length === 1 ? "" : "s"} remained linked and stayed selected. Try again or contact support if this continues.`,
                });
            } else if (unlinkedCount === 1) {
                Swal.fire("Deleted!", "Your module has been removed.", "success");
            } else if (unlinkedCount > 0) {
                Swal.fire("Deleted!", `${unlinkedCount} modules have been removed.`, "success");
            }
        } finally {
            /* Keeps the final "done" step visible briefly instead of the progress bar vanishing instantly; cleared on unmount above so it can't set state on a gone component. */
            progressTimeoutRef.current = window.setTimeout(() => setProgress(null), 250);
        }
    };

    return {
        selectedIds,
        toggleRow,
        toggleAllMatching,
        allMatchingSelected,
        someMatchingSelected,
        isProcessing,
        progress,
        cancelRemaining,
        confirmation,
        setConfirmation,
        startConfirmation,
        runConfirmedUnlink,
    };
}

export default useVersionModuleBulkActions;
